<template>
  <div class="app-select" :class="{ glass: appearance === 'glass', active, disabled }">
    <button
      ref="trigger"
      type="button"
      v-bind="$attrs"
      role="combobox"
      aria-haspopup="listbox"
      :aria-label="label"
      :aria-expanded="String(open)"
      :aria-controls="listId"
      :aria-activedescendant="open && activeIndex >= 0 ? listId + '-' + activeIndex : null"
      :disabled="disabled"
      :title="selected ? selected.title || selected.label : placeholder"
      @click="toggle"
      @keydown="onKeydown"
    >
      <ellipsis-tooltip :text="selected ? selected.label : placeholder" />
      <span class="app-select-arrow" aria-hidden="true"></span>
    </button>
    <div
      v-show="open"
      ref="panel"
      class="app-select-panel"
      :style="panelStyle"
      :id="listId"
      role="listbox"
      :aria-label="label"
      :aria-busy="String(loading)"
      @mousedown.prevent
    >
      <div v-if="loading" class="app-select-message" role="status">正在加载...</div>
      <div v-else-if="!options.length" class="app-select-message">暂无可选项</div>
      <div
        v-for="(option, index) in options"
        :key="String(option.value)"
        :id="listId + '-' + index"
        role="option"
        :aria-selected="String(same(option.value, value))"
        :aria-disabled="String(!!option.disabled)"
        class="app-select-option"
        :class="{
          highlighted: index === activeIndex,
          selected: same(option.value, value),
          disabled: option.disabled
        }"
        @mouseenter="activeIndex = index"
        @click="choose(index)"
      >
        <ellipsis-tooltip ref="optionText" :text="option.label" />
        <span v-if="same(option.value, value)" aria-hidden="true">✓</span>
      </div>
    </div>
  </div>
</template>
<script>
import EllipsisTooltip from '../ellipsis-tooltip/ellipsis-tooltip'
let nextId = 0
export default {
  inheritAttrs: false,
  components: { EllipsisTooltip },
  props: {
    value: [String, Number],
    options: { type: Array, default: () => [] },
    label: { type: String, required: true },
    placeholder: { type: String, default: '请选择' },
    disabled: Boolean,
    loading: Boolean,
    active: Boolean,
    appearance: { type: String, default: 'plain' }
  },
  data() {
    return {
      open: false,
      activeIndex: -1,
      panelStyle: {},
      listId: 'app-select-' + ++nextId,
      searchText: '',
      searchTimer: null
    }
  },
  computed: {
    selected() {
      return this.options.find(option => this.same(option.value, this.value))
    }
  },
  watch: {
    loading() {
      if (this.open) this.$nextTick(this.position)
    },
    disabled(value) {
      if (value) this.close()
    },
    value() {
      if (this.open) this.resetActive()
    },
    options() {
      if (this.open) {
        this.resetActive()
        this.$nextTick(this.position)
      }
    }
  },
  mounted() {
    document.body.appendChild(this.$refs.panel)
    document.addEventListener('pointerdown', this.onOutside, true)
    window.addEventListener('resize', this.close)
    window.addEventListener('scroll', this.onScroll, true)
  },
  deactivated() {
    this.close()
  },
  beforeDestroy() {
    this.close()
    document.removeEventListener('pointerdown', this.onOutside, true)
    window.removeEventListener('resize', this.close)
    window.removeEventListener('scroll', this.onScroll, true)
    const panel = this.$refs.panel
    if (panel && panel.parentNode) panel.parentNode.removeChild(panel)
  },
  methods: {
    same(a, b) {
      return a != null && b != null && String(a) === String(b)
    },
    resetActive() {
      const selected = this.options.findIndex(
        option => this.same(option.value, this.value) && !option.disabled
      )
      this.activeIndex =
        selected >= 0 ? selected : this.options.findIndex(option => !option.disabled)
      this.$nextTick(this.revealActive)
    },
    toggle() {
      if (this.open) this.close()
      else this.show()
    },
    show() {
      if (this.disabled) return
      this.open = true
      this.resetActive()
      this.$nextTick(() => {
        this.position()
        this.revealActive()
      })
    },
    close() {
      this.open = false
      this.searchText = ''
      clearTimeout(this.searchTimer)
      ;(this.$refs.optionText || []).forEach(item => item.hide())
    },
    choose(index) {
      const option = this.options[index]
      if (this.disabled || this.loading || !option || option.disabled) return
      if (!this.same(option.value, this.value)) {
        this.$emit('input', option.value)
        this.$emit('change', option.value)
      }
      this.close()
      this.$refs.trigger.focus()
    },
    position() {
      if (!this.open) return
      const rect = this.$refs.trigger.getBoundingClientRect()
      const below = window.innerHeight - rect.bottom - 12
      const above = rect.top - 12
      const upwards = below < Math.min(320, this.$refs.panel.scrollHeight) && above > below
      const maxHeight = Math.max(0, Math.min(320, upwards ? above : below))
      const width = Math.min(Math.max(rect.width, 160), window.innerWidth - 16)
      this.panelStyle = {
        width: width + 'px',
        maxHeight: maxHeight + 'px',
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)) + 'px',
        top: upwards ? 'auto' : Math.max(8, rect.bottom + 4) + 'px',
        bottom: upwards ? Math.max(8, window.innerHeight - rect.top + 4) + 'px' : 'auto'
      }
    },
    revealActive() {
      if (!this.open || this.activeIndex < 0) return
      const panel = this.$refs.panel
      const item = panel.querySelector('[id="' + this.listId + '-' + this.activeIndex + '"]')
      if (!item) return
      if (item.offsetTop < panel.scrollTop) panel.scrollTop = item.offsetTop
      else if (item.offsetTop + item.offsetHeight > panel.scrollTop + panel.clientHeight)
        panel.scrollTop = item.offsetTop + item.offsetHeight - panel.clientHeight
    },
    move(step) {
      const enabled = this.options
        .map((option, index) => (option.disabled ? -1 : index))
        .filter(index => index >= 0)
      if (!enabled.length) return
      const current = enabled.indexOf(this.activeIndex)
      this.activeIndex = enabled[(current + step + enabled.length) % enabled.length]
      this.$nextTick(this.revealActive)
    },
    onKeydown(event) {
      const key = event.key
      if (key === 'Tab') {
        this.close()
        return
      }
      if (key === 'Escape') {
        if (this.open) {
          event.preventDefault()
          event.stopPropagation()
          this.close()
        }
        return
      }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(key)) {
        event.preventDefault()
        if (!this.open) {
          this.show()
          return
        }
        if (key === 'Enter' || key === ' ') this.choose(this.activeIndex)
        else if (key === 'ArrowDown' || key === 'ArrowUp') this.move(key === 'ArrowDown' ? 1 : -1)
        else {
          const enabled = this.options
            .map((option, index) => (option.disabled ? -1 : index))
            .filter(index => index >= 0)
          this.activeIndex = key === 'Home' ? enabled[0] : enabled[enabled.length - 1]
          this.$nextTick(this.revealActive)
        }
      } else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if (!this.open) this.show()
        clearTimeout(this.searchTimer)
        this.searchText += key.toLocaleLowerCase()
        const index = this.options.findIndex(
          option =>
            !option.disabled &&
            String(option.label)
              .toLocaleLowerCase()
              .startsWith(this.searchText)
        )
        if (index >= 0) {
          this.activeIndex = index
          this.$nextTick(this.revealActive)
        }
        this.searchTimer = setTimeout(() => {
          this.searchText = ''
        }, 600)
      }
    },
    onOutside(event) {
      if (this.open && !this.$el.contains(event.target) && !this.$refs.panel.contains(event.target))
        this.close()
    },
    onScroll(event) {
      if (this.open && !this.$refs.panel.contains(event.target)) this.close()
    }
  }
}
</script>
<style>
.app-select {
  display: inline-block;
  min-width: 0;
  max-width: 100%;
  vertical-align: middle;
}
.app-select > button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 32px;
  padding: 5px 12px;
  border: 1px solid #d4d8df;
  border-radius: 18px;
  background: white;
  color: #666;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}
.app-select > button > .ellipsis_tooltip_wrap {
  flex: 1;
  text-align: left;
}
.app-select > button:focus {
  outline: none;
}
.app-select > button:focus-visible {
  border-color: var(--primary-color);
}
.app-select.glass > button {
  border-color: rgba(255, 255, 255, 0.42);
  background: rgba(255, 255, 255, 0.035);
  color: rgba(255, 255, 255, 0.68);
}
.app-select.glass.active > button,
.app-select.glass > button[aria-expanded='true'] {
  border-color: rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.22);
  color: white;
}
.app-select.disabled {
  opacity: 0.5;
}
.app-select-arrow {
  flex: none;
  width: 7px;
  height: 7px;
  border: solid currentColor;
  border-width: 0 1px 1px 0;
  transform: translateY(-2px) rotate(45deg);
}
.app-select-panel {
  position: fixed;
  z-index: 100050;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
  padding: 4px;
  border: 1px solid #dce1e8;
  border-radius: 10px;
  background: #fff;
  color: #282828;
  box-shadow: 0 6px 20px #0003;
}
.app-select-option {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 6px 10px;
  box-sizing: border-box;
  border-radius: 6px;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
}
.app-select-option > .ellipsis_tooltip_wrap {
  flex: 1;
}
.app-select-option.highlighted {
  background: #edf2ff;
}
.app-select-option.selected {
  color: var(--primary-color);
}
.app-select-option.disabled {
  opacity: 0.4;
  cursor: default;
}
.app-select-message {
  padding: 12px;
  color: #8a8f98;
  font-size: 12px;
}
</style>
